import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePageView } from "../../../hooks/usePageView";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  getInsight,
  createInsight,
  updateInsight,
  publishInsight,
} from "../../../lib/expert_insights_api";
import { INSIGHT_TITLE_MAX_WORDS, INSIGHT_BODY_MAX_WORDS } from "@shared/types/expert_insights/api";
import { useAsyncData } from "../../../hooks/useAsyncData";
import { useAsyncHandler } from "../../../hooks/useAsyncHandler";
import { useContainsLinks } from "../../../hooks/useContainsLinks";
import { useUserContext } from "../../../context/UserContext";
import { ROUTES } from "../../../route_paths";

export { INSIGHT_TITLE_MAX_WORDS as TITLE_WORD_LIMIT, INSIGHT_BODY_MAX_WORDS as BODY_WORD_LIMIT };

function countWords(text: string): number {
  return text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
}

export interface UseInsightEditorResult {
  isEditMode: boolean;
  isPublishedWithDraft: boolean;
  title: string;
  body: string;
  fields: string[];
  titleWordCount: number;
  bodyWordCount: number;
  loading: boolean;
  savingDraft: boolean;
  publishing: boolean;
  saveError: string | null;
  publishError: string | null;
  /** True when the form has valid content and no async operation is in progress. */
  canSave: boolean;
  /** True when canSave is true AND at least one field is selected. Gates the Publish button. */
  canPublish: boolean;
  /** True when the debounced title contains a URL. */
  titleHasLinks: boolean;
  /** True when the debounced body contains a URL. */
  bodyHasLinks: boolean;
  /** Field URNs the author is authorized to use, from their profile. */
  authorizedFields: string[];
  setTitle: (v: string) => void;
  setBody: (v: string) => void;
  setFields: (v: string[]) => void;
  /** Edit mode: saves in place + toast. Create mode: creates + navigates to edit. */
  handleSaveDraft: () => Promise<void>;
  /** Both modes: saves current form as draft (upsert), then publishes + navigates to list. */
  handlePublish: () => Promise<void>;
  handleCancel: () => void;
}

/** Manages state for the insight editor in both create and edit modes. */
export function useInsightEditor(): UseInsightEditorResult {
  const { id } = useParams<{ id?: string }>();
  usePageView(id ? "expert-insights/edit" : "expert-insights/new");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useUserContext();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [fields, setFields] = useState<string[]>([]);

  const {
    data: existingInsight,
    loading,
    refresh: refreshLocalInsight,
  } = useAsyncData(() => (id ? getInsight(id) : Promise.resolve(null)));

  useEffect(() => {
    if (!existingInsight) return;
    const source = existingInsight.draft ?? existingInsight.currentVersion;
    if (source) {
      setTitle(source.title);
      setBody(source.body);
    }
    setFields(existingInsight.fields);
  }, [existingInsight]);

  const titleWordCount = countWords(title);
  const bodyWordCount = countWords(body);
  const titleHasLinks = useContainsLinks(title);
  const bodyHasLinks = useContainsLinks(body);
  const isFormValid =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    titleWordCount <= INSIGHT_TITLE_MAX_WORDS &&
    bodyWordCount <= INSIGHT_BODY_MAX_WORDS &&
    !titleHasLinks &&
    !bodyHasLinks;

  const {
    loading: savingDraft,
    error: saveError,
    handle: handleSaveDraft,
  } = useAsyncHandler(async () => {
    if (id) {
      await updateInsight(id, { title, body, fields });
      await refreshLocalInsight();
      enqueueSnackbar(t("expertInsights.editor.savedToast"), { variant: "success" });
    } else {
      const created = await createInsight({ title, body, fields });
      navigate(ROUTES.expertInsights.insights.edit.path({ id: created.id }), { replace: true });
    }
  });

  const {
    loading: publishing,
    error: publishError,
    handle: handlePublish,
  } = useAsyncHandler(async () => {
    if (id) {
      await updateInsight(id, { title, body, fields });
      await publishInsight(id);
    } else {
      const created = await createInsight({ title, body, fields });
      await publishInsight(created.id);
    }
    enqueueSnackbar(t("expertInsights.editor.publishedToast"), { variant: "success" });
    navigate(ROUTES.expertInsights.insights.path());
  });

  const anySaving = savingDraft || publishing;
  const hasDraft = existingInsight !== null && existingInsight?.draft !== null;
  const hasPublishedVersion = existingInsight !== null && existingInsight?.currentVersion !== null;

  return {
    isEditMode: Boolean(id),
    isPublishedWithDraft: hasPublishedVersion && hasDraft,
    title,
    body,
    fields,
    titleWordCount,
    bodyWordCount,
    loading,
    savingDraft,
    publishing,
    saveError,
    publishError,
    canSave: isFormValid && !anySaving,
    canPublish: isFormValid && !anySaving && fields.length > 0,
    titleHasLinks,
    bodyHasLinks,
    authorizedFields: profile?.fields ?? [],
    setTitle,
    setBody,
    setFields,
    handleSaveDraft,
    handlePublish,
    handleCancel: () => navigate(ROUTES.expertInsights.insights.path()),
  };
}
